import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import KIT_CONFIG from './kit.config';

// ── MASTER-QUELLE (design-system/kit/kit.master.js) ─────────────────────────────────────
// Diese Datei wird BYTE-IDENTISCH als src/ui/kit.js in admin-app UND partner-portal
// synchronisiert: `node design-system/kit/sync.js` (mit `--check` nur Drift melden).
// NIE die Kopien direkt editieren — immer den Master ändern und neu synchronisieren;
// der Drift-Wächter (src/ui/kit.drift.test.js) macht Abweichungen in jeder App rot.
// Bewusste App-Unterschiede leben in src/ui/kit.config.js (liegt NEBEN der Kopie):
//   { bg:          Seitenhintergrund ('#f5f5f5' Admin, '#f7f7f8' Partner — MASTER.md),
//     classPrefix: null (Admin: KEIN eigenes className-Attribut) | 'pp' (Partner: pp-*-Hooks),
//     statVariant: 'regular' (Admin) | 'compact' (Partner, inkl. bg-Prop) }

// Gemeinsame Design-Tokens & Bausteine – NUR Optik. Vereinheitlicht Radien, CI-Farben
// und Status-Darstellung über alle Seiten (gegen den Inline-Style-Drift).

const { bg: APP_BG, classPrefix: CLASS_PREFIX, statVariant: STAT_VARIANT } = KIT_CONFIG;

// className-Politik: ohne classPrefix entsteht KEIN className-Attribut, das der Aufrufer
// nicht selbst übergeben hat; mit classPrefix exakt die heutigen `pp-*`-Hooks der index.css.
const p = (name) => (CLASS_PREFIX ? `${CLASS_PREFIX}-${name}` : '');
const mod = (flag, name) => (CLASS_PREFIX && flag ? name : '');
const cx = (...parts) => {
  const value = parts.filter(Boolean).join(' ').trim();
  return value ? { className: value } : {};
};

export const R = { sm: 8, md: 10, lg: 12, xl: 16, pill: 999 };

export const C = {
  brand: '#9C27B0', brandTint: '#f3e5f5',
  ink: '#0a0a0a', text: '#374151', muted: '#4b5563', faint: '#6b7280',
  line: '#e5e7eb', bg: APP_BG, card: '#ffffff',
};

// Zentrale Fahrt-Status (Label + Farbe) – statt pro Seite neu definiert.
export const statusColor = { searching: '#4b5563', accepted: '#4b5563', arrived: '#4b5563', in_progress: '#9C27B0', completed: '#4b5563', cancelled: '#ef4444', scheduled: '#4b5563' };
export const statusLabel = { searching: 'Suche', accepted: 'Angenommen', arrived: 'Angekommen', in_progress: 'Unterwegs', completed: 'Abgeschlossen', cancelled: 'Storniert', scheduled: 'Reserviert' };

// Einheitliche Karte (weiß, Hairline, 12px Radius).
export const Card = ({ children, style, className = '', ...rest }) => (
  <div {...rest} {...cx(p('card'), className)} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: R.lg, padding: 16, ...style }}>{children}</div>
);

export const SectionLabel = ({ children, style }) => (
  <div {...cx(p('section-label'))} style={{ fontSize: 12, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, ...style }}>{children}</div>
);

export const Badge = ({ color = C.muted, children }) => (
  <span {...cx(p('badge'))} style={{ fontSize: 12, fontWeight: 700, color, background: `${color}1a`, padding: '3px 10px', borderRadius: R.pill, whiteSpace: 'nowrap' }}>{children}</span>
);

// Skeleton-Bausteine für Ladezustände (Shimmer via .vg-sk in index.css).
export const Skeleton = ({ w = '100%', h = 14, r = 8, style }) => (
  <div className="vg-sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

export const SkeletonCard = () => (
  <div {...cx(p('card'), p('skeleton-card'))} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: R.lg, padding: 14 }}>
    <Skeleton w={70} h={9} /><div style={{ height: 10 }} /><Skeleton w={90} h={20} />
  </div>
);

export const SkeletonDashboard = ({ cards = 6 }) => (
  <div>
    <Skeleton w={180} h={26} /><div style={{ height: 22 }} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 22 }}>
      {Array.from({ length: cards }).map((_, i) => <SkeletonCard key={i} />)}
    </div>
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: R.xl, padding: 20 }}>
      <Skeleton w={160} h={16} /><div style={{ height: 16 }} /><Skeleton w="100%" h={200} r={10} />
    </div>
  </div>
);

// Skeleton-Zeilen für Tabellen-Ladezustände.
export const SkeletonTableRows = ({ rows = 4, cols = 1 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={r}>
        {Array.from({ length: cols }).map((_, c) => (
          <td key={c} style={{ padding: '12px 14px' }}><Skeleton h={12} /></td>
        ))}
      </tr>
    ))}
  </>
);

// Generischer Seiten-Ladezustand (Titel + Karte).
export const SkeletonPage = () => (
  <div>
    <Skeleton w={180} h={24} /><div style={{ height: 18 }} />
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: R.lg, padding: 18 }}>
      <Skeleton w={140} h={14} /><div style={{ height: 14 }} /><Skeleton w="100%" h={140} r={10} />
    </div>
  </div>
);

// Kennzahl-Kachel — optional klickbar (Hover-Rahmen + ↗ signalisiert Drill-down).
// Zwei bewusste Ausprägungen (statVariant): 'regular' (Admin, ruhig/größer) und
// 'compact' (Partner, dichter, uppercase-Label, akzeptiert zusätzlich die bg-Prop).
const StatRegular = ({ label, value, sub, color = C.ink, icon: Icon, onClick }) => {
  const [h, setH] = React.useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ position: 'relative', background: C.card, borderRadius: R.lg, padding: 15, cursor: onClick ? 'pointer' : 'default', transition: 'border-color .15s', border: `1px solid ${onClick && h ? C.ink : C.line}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: -0.5 }}>{value}</div>
          {sub && <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>{sub}</div>}
        </div>
        {Icon && <div style={{ width: 36, height: 36, borderRadius: R.md, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon size={18} color={C.muted} /></div>}
      </div>
      {onClick && <ArrowUpRight size={14} color={h ? C.ink : '#d1d5db'} style={{ position: 'absolute', bottom: 10, right: 12 }} />}
    </div>
  );
};

const StatCompact = ({ label, value, sub, color = C.ink, bg = '#f4f5f7', icon: Icon, onClick }) => {
  const [h, setH] = React.useState(false);
  return (
    <div {...cx(p('stat'), mod(onClick, 'is-interactive'))} onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ position: 'relative', background: C.card, borderRadius: R.lg, padding: 14, border: `1px solid ${onClick && h ? C.ink : C.line}`, cursor: onClick ? 'pointer' : 'default', transition: 'border-color .15s' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: C.faint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>{label}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color, letterSpacing: -0.5 }}>{value}</div>
          {sub && <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>{sub}</div>}
        </div>
        {Icon && <div style={{ width: 32, height: 32, borderRadius: 9, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon size={16} color={C.muted} /></div>}
      </div>
      {onClick && <ArrowUpRight size={13} color={h ? C.ink : '#d1d5db'} style={{ position: 'absolute', bottom: 8, right: 10 }} />}
    </div>
  );
};

export const Stat = (props) => (STAT_VARIANT === 'compact' ? <StatCompact {...props} /> : <StatRegular {...props} />);

// Einheitlicher recharts-Stil: ruhig, CI-Violett, weiche Achsen/Tooltip.
export const CHART = { brand: C.brand, ink: C.ink, grid: '#eef0f3', axis: C.faint, barRadius: [6, 6, 0, 0] };
export const chartAxis = { tick: { fill: C.faint, fontSize: 12 }, axisLine: false, tickLine: false };
export const chartTooltip = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,.08)' };

// Fortschritts-/Verteilungsbalken (runde Spur + gefüllter Anteil). Default Ink (Violett sehr minimal).
export const MeterBar = ({ value = 0, max = 100, color = C.ink, track = '#eef0f3', height = 8 }) => (
  <div {...cx(p('meter'))} style={{ height, background: track, borderRadius: 999, overflow: 'hidden' }}>
    <div style={{ height: '100%', width: `${Math.max(0, Math.min((value / (max || 1)) * 100, 100))}%`, background: color, borderRadius: 999, transition: 'width .5s' }} />
  </div>
);

// ── Einheitliche Buttons (design-system/MASTER.md): primary=Ink, secondary=weiß/Hairline,
//    danger=Rot (nur destruktiv), ghost=transparent. Hover/Active/Disabled global via index.css. ──
export const Btn = ({ variant = 'primary', children, style, className = '', ...rest }) => {
  const v = {
    primary:   { background: C.ink, color: '#fff', border: `1px solid ${C.ink}` },
    secondary: { background: '#fff', color: C.text, border: `1px solid ${C.line}` },
    danger:    { background: '#ef4444', color: '#fff', border: '1px solid #ef4444' },
    ghost:     { background: 'transparent', color: C.muted, border: '1px solid transparent' },
  }[variant] || {};
  return (
    <button {...rest} {...cx(p('btn'), mod(true, `is-${variant}`), className)} style={{ ...v, borderRadius: R.md, padding: '9px 14px', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, ...style }}>{children}</button>
  );
};

// Leerer Zustand: nie leere Fläche — Icon + Satz + optionale Aktion.
export const Empty = ({ icon: Icon, title = 'Nichts gefunden', hint, action, style }) => (
  <div {...cx(p('empty-state'))} style={{ padding: '42px 20px', textAlign: 'center', color: C.faint, ...style }}>
    {Icon && <Icon size={26} style={{ marginBottom: 10 }} />}
    <div style={{ fontSize: 14, fontWeight: 700, color: C.muted }}>{title}</div>
    {hint && <div style={{ fontSize: 13, marginTop: 4 }}>{hint}</div>}
    {action && <div style={{ marginTop: 14 }}>{action}</div>}
  </div>
);

// Fehlerzustand mit klarem Weg zurück („Erneut versuchen").
export const ErrorBox = ({ text = 'Konnte nicht geladen werden.', onRetry, style }) => (
  <div {...cx(p('error-box'))} style={{ padding: 14, borderRadius: R.md, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, ...style }}>
    <span>{text}</span>
    {onRetry && <button onClick={onRetry} style={{ background: '#fff', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 700 }}>Erneut versuchen</button>}
  </div>
);

// Bestätigungs-Dialog (statt window.confirm) — Card-Optik, Escape/Klick-außen bricht ab,
// optionale Änderungs-Tabelle (Feld → neuer Wert) für Engine-/Rabatt-Vorschläge.
// Aktuell nur im Admin in Gebrauch; im Partner-Portal ein bewusst toter Export.
export function ConfirmModal({ open, title, body, changes, confirmLabel = 'Bestätigen', danger = false, onConfirm, onCancel }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onCancel && onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" onMouseDown={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,10,25,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
      <div onMouseDown={(e) => e.stopPropagation()}
        style={{ background: C.card, borderRadius: R.xl, border: `1px solid ${C.line}`, padding: 22, width: 'min(460px, 100%)', boxShadow: '0 24px 60px -20px rgba(20,10,40,.5)' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.ink, marginBottom: 8, letterSpacing: -0.2 }}>{title}</div>
        {body && <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{body}</div>}
        {changes && changes.length > 0 && (
          <table style={{ marginTop: 12, width: '100%', fontSize: 13, borderCollapse: 'collapse' }}><tbody>
            {changes.map(([k, v], i) => (
              <tr key={i} style={{ background: i % 2 ? '#faf9fc' : 'transparent' }}>
                <td style={{ padding: '6px 10px', color: C.muted, fontWeight: 700, whiteSpace: 'nowrap' }}>{k}</td>
                <td style={{ padding: '6px 4px', color: C.faint }}>→</td>
                <td style={{ padding: '6px 10px', color: C.ink, fontWeight: 800, fontFamily: 'ui-monospace, monospace', width: '100%' }}>{v}</td>
              </tr>
            ))}
          </tbody></table>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <Btn variant="secondary" onClick={onCancel}>Abbrechen</Btn>
          <Btn variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>{confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
}
